'use client';
// ═══════════════════════════════════════════════════════════════════════════
// AUREUS SOCIAL PRO — Global Error Boundary
// Capture les erreurs de rendu React au niveau racine et les envoie à Sentry.
// Remplace le layout racine quand une erreur non gérée survient.
// ═══════════════════════════════════════════════════════════════════════════
import * as Sentry from '@sentry/nextjs';
import { useEffect, useRef } from 'react';

export default function GlobalError({ error, reset }) {
  const feedbackBtnRef = useRef(null);

  useEffect(() => {
    Sentry.captureException(error);
    // ⚠️ AUSSI dans NOTRE table error_logs (incident 24/08/2026) : l'app entière
    // est tombée en boot-crash pendant ~40 min, l'écran disait « incident
    // enregistré » — mais seul Sentry l'avait. error_logs était VIDE, le
    // diagnostic a dû se faire à l'aveugle. La route /api/error-logs écrit via
    // le service role (pas d'auth requise : le crash arrive souvent AVANT le
    // login) et déclenche l'alerte Telegram « boot-crash » au 3e crash en 15 min.
    try {
      fetch('/api/error-logs', {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          level: 'fatal',
          source: 'global-error-boundary',
          message: String(error?.message || error || 'Erreur inconnue').slice(0, 1500),
          stack: String(error?.stack || '').slice(0, 1800),
          url: typeof window !== 'undefined' ? window.location.href : '',
        }),
      }).catch(() => {});
    } catch (_) { /* le rapport d'incident ne doit jamais re-crasher l'écran d'incident */ }
  }, [error]);

  // Attache le widget Feedback au bouton (API v10 attachTo)
  useEffect(() => {
    const feedback = Sentry.getFeedback?.();
    if (!feedback || !feedbackBtnRef.current) return;
    const unsubscribe = feedback.attachTo(feedbackBtnRef.current);
    return () => { try { unsubscribe?.(); } catch { /* */ } };
  }, []);

  return (
    <html lang="fr">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          background: '#0a0e1a',
          color: '#e8eaed',
          padding: 24,
        }}
      >
        <div style={{ textAlign: 'center', maxWidth: 480 }}>
          <h1 style={{ fontSize: 22, marginBottom: 12, color: '#d4af37' }}>
            Une erreur est survenue
          </h1>
          <p style={{ fontSize: 15, lineHeight: 1.5, opacity: 0.8, marginBottom: 24 }}>
            L&apos;incident a été enregistré automatiquement. Vous pouvez réessayer
            ou nous dire ce que vous faisiez juste avant.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => reset()}
              style={{
                background: '#d4af37',
                color: '#0a0e1a',
                border: 'none',
                borderRadius: 8,
                padding: '10px 24px',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Réessayer
            </button>
            <button
              ref={feedbackBtnRef}
              style={{
                background: 'transparent',
                color: '#d4af37',
                border: '1px solid #d4af37',
                borderRadius: 8,
                padding: '10px 24px',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Signaler le problème
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
